<template>
  <div>
    <Row class="mb-2" :gutter="16">
      <Col span="24">
        <strong>{{
          $l("components.settings.licenseInfor", "License information")
        }}</strong>
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="5">
        <span>{{ $l("components.settings.product", "Product") + ":" }}</span>
      </Col>
      <Col>
        <span>CyRadar Endpoint Security Server</span>
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="5">
        <span>{{
          $l("components.settings.copyright", "Copyright") + ":"
        }}</span>
      </Col>
      <Col>
        <span>CyRadar, JSC</span>
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="5">
        <span>{{
          $l("components.settings.serverUpdate", "Server update") + ":"
        }}</span>
      </Col>
      <Col>
        <span class="mr-3">{{
          information.server && information.server.length > 0
            ? information.server.join(", ")
            : $l("components.settings.empty", "N/A")
        }}</span>

        <span @click="showUpdateServer">
          <Icon type="md-create" />
        </span>
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="5">
        <span>{{
          $l("components.settings.licenseCode", "License code") + ":"
        }}</span>
      </Col>
      <Col>
        <span>{{
          information.license
            ? information.license
            : $l("components.settings.empty", "N/A")
        }}</span>
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="5">
        <span>{{
          $l("components.settings.expirationDate", "Expiration date") + ":"
        }}</span>
      </Col>
      <Col>
        <span
          :class="{ 'is-danger': dateDistance(information.expiredAt) <= 0 }"
          >{{
            information.expiredAt &&
            information.expiredAt !== "0001-01-01T00:00:00Z"
              ? renderTime(information.expiredAt)
              : $l("components.settings.empty", "N/A")
          }}</span
        >
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="5">
        <span>{{ $l("components.settings.maxAgent", "Max agent") + ":" }}</span>
      </Col>
      <Col>
        <span>{{
          information.agentLimit && information.agentLimit !== -1
            ? information.agentLimit
            : information.agentLimit && information.agentLimit === -1
            ? $l("components.settings.unlimit", "Unlimit")
            : $l("components.settings.empty", "N/A")
        }}</span>
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="5">
        <span>{{
          $l("components.settings.maxEDRServer", "Max EDR server") + ":"
        }}</span>
      </Col>
      <Col>
        <span>{{
          information.serverLimit && information.serverLimit !== -1
            ? information.serverLimit
            : information.serverLimit && information.serverLimit === -1
            ? $l("components.settings.unlimit", "Unlimit")
            : $l("components.settings.empty", "N/A")
        }}</span>
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="5">
        <span>{{
          $l("components.settings.maxLevelEDRServer", "Max level EDR server") +
          ":"
        }}</span>
      </Col>
      <Col>
        <span>{{
          information.treeHeight
            ? information.treeHeight
            : $l("components.settings.empty", "N/A")
        }}</span>
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="5">
        <span>{{
          $l("components.settings.proxyServer", "Proxy server") + ":"
        }}</span>
      </Col>
      <Col>
        <span class="mr-3">{{
          information.proxyServer
            ? information.proxyServer
            : $l("components.settings.empty", "N/A")
        }}</span>

        <span @click="showUpdateProxy">
          <Icon type="md-create" />
        </span>
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="5">
        <span>{{
          $l("components.settings.serverStatus", "Server status") + ":"
        }}</span>
      </Col>
      <Col>
        <span
          class="mr-3"
          :class="{
            'is-danger': !information.status,
            'is-success': information.status,
          }"
          >{{
            information.status
              ? $l("components.settings.online", "Online")
              : $l("components.settings.offline", "Offline")
          }}</span
        >
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="5">
        <span>{{
          $l("components.settings.periodTime", "Period time") + ":"
        }}</span>
      </Col>
      <Col>
        <span class="mr-3">{{
          information.interval && information.unit
            ? information.unit === "minutes"
              ? information.interval +
                " " +
                $l("components.settings.minutes", "minutes")
              : information.interval +
                " " +
                $l("components.settings.hours", "hours")
            : $l("components.settings.empty", "N/A")
        }}</span>

        <span @click="showUpdateTime">
          <Icon type="md-create" />
        </span>
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="24">
        <strong>{{
          $l("components.settings.updateLicense", "Update license")
        }}</strong>
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="24">
        <span>
          {{
            $l(
              "components.settings.updateLicenseText1",
              "To use another license, please select "
            )
          }}
          <strong>{{
            $l("components.settings.updateLicenseText2", "Change license ")
          }}</strong>
          {{ $l("components.settings.updateLicenseText3", "below.") }} <br />
          {{
            $l(
              "components.settings.updateLicenseText4",
              "In case you do not have a license, please contact hotline (+84) 24 7300 6066 or "
            )
          }}
          <u>{{
            $l("components.settings.updateLicenseText5", "contact@cyradar.com.")
          }}</u>
        </span>
      </Col>
    </Row>

    <Row class="mb-2" :gutter="16">
      <Col span="6">
        <span class="mr-3">
          <Icon size="20" type="md-cart" />
        </span>

        <span @click="modalUpdateCopyrightOnline = true" class="button-text">{{
          $l("components.settings.updateLicenseOnline", "Update license online")
        }}</span>
      </Col>

      <Col span="6">
        <span class="mr-3">
          <Icon size="20" type="md-key" />
        </span>

        <span @click="modalUpdateCopyrightOffline = true" class="button-text">{{
          $l(
            "components.settings.updateLicenseOffline",
            "Update license offline"
          )
        }}</span>
      </Col>

      <Col span="6">
        <span class="mr-3">
          <Icon size="20" type="md-git-compare" />
        </span>

        <span @click="showCheckConnection" class="button-text">{{
          $l("components.settings.checkConnection", "Check connection")
        }}</span>
      </Col>
    </Row>

    <!-- Update server -->

    <modal :show.sync="modalUpdateServer">
      <box>
        <div class="close-icon" @click="modalUpdateServer = false">
          <Icon size="25" type="ios-close" />
        </div>
        <div style="text-align: center; font-weight: bold">
          <span>{{
            $l("components.settings.updateServer", "Update server")
          }}</span>
        </div>

        <div style="margin: auto; width: 90%">
          <Form
            ref="formUpdateServer"
            :model="formUpdateServer"
            :rules="ruleValidate"
            label-position="top"
          >
            <FormItem ref="serverUpdate" prop="serverUpdate">
              <template slot="label">
                <strong>{{
                  $l("components.settings.serverUpdate", "Server update")
                }}</strong>
              </template>

              <Input
                v-model="formUpdateServer.serverUpdate"
                placeholder="https://api.edr.server.com:6767"
              />
            </FormItem>

            <FormItem>
              <div class="field is-grouped is-grouped-right">
                <div class="control">
                  <Button class="mr-2" @click="modalUpdateServer = false">{{
                    $l("global.cancel", "Cancel")
                  }}</Button>
                </div>
                <div class="control">
                  <Button
                    @click="handleClickUpdateServer('formUpdateServer')"
                    type="primary"
                  >
                    {{ $l("global.update", "Update") }}</Button
                  >
                </div>
              </div>
            </FormItem>
          </Form>
        </div>
      </box>
    </modal>

    <!-- Update Proxy -->

    <modal :show.sync="modalUpdateProxy">
      <box>
        <div class="close-icon" @click="modalUpdateProxy = false">
          <Icon size="25" type="ios-close" />
        </div>
        <div style="text-align: center; font-weight: bold">
          <span>{{
            $l("components.settings.updateProxy", "Update proxy")
          }}</span>
        </div>

        <div style="margin: auto; width: 90%">
          <Form
            ref="formUpdateProxy"
            :model="formUpdateProxy"
            :rules="ruleValidate"
            label-position="top"
          >
            <FormItem ref="proxy" prop="proxy">
              <template slot="label">
                <strong>{{
                  $l("components.settings.proxyServer", "Proxy serer")
                }}</strong>
              </template>

              <Input
                v-model="formUpdateProxy.proxy"
                placeholder="https://user:pass@192.189.1.1:6767"
              />
            </FormItem>

            <FormItem>
              <div class="field is-grouped is-grouped-right">
                <div class="control">
                  <Button class="mr-2" @click="modalUpdateProxy = false">{{
                    $l("global.cancel", "Cancel")
                  }}</Button>
                </div>
                <div class="control">
                  <Button
                    @click="handleClickUpdateProxy('formUpdateProxy')"
                    type="primary"
                  >
                    {{ $l("global.update", "Update") }}</Button
                  >
                </div>
              </div>
            </FormItem>
          </Form>
        </div>
      </box>
    </modal>

    <!-- Update time -->

    <modal :show.sync="modalUpdateTime">
      <box>
        <div class="close-icon" @click="modalUpdateTime = false">
          <Icon size="25" type="ios-close" />
        </div>
        <div style="text-align: center; font-weight: bold">
          <span>{{
            $l("components.settings.updateProxy", "Update proxy")
          }}</span>
        </div>

        <div style="margin: auto; width: 90%; min-height: 300px">
          <Form
            ref="formUpdateTime"
            :model="formUpdateTime"
            :rules="ruleValidate"
            label-position="top"
          >
            <FormItem>
              <strong>{{
                $l("components.settings.updatePeriod", "Update period")
              }}</strong>
            </FormItem>

            <Row :gutter="16">
              <Col span="12">
                <FormItem ref="interval" prop="interval">
                  <Select
                    v-model="formUpdateTime.interval"
                    :placeholder="
                      $l('components.settings.interval', 'Interval')
                    "
                  >
                    <Option
                      v-for="item in formUpdateTime.unit === 'hours'
                        ? listIntervalHours
                        : listIntervalMinutes"
                      :value="item"
                      :key="item"
                      >{{ item }}</Option
                    >
                  </Select>
                </FormItem>
              </Col>
              <Col span="12">
                <FormItem
                  ref="unit"
                  prop="unit"
                  :placeholder="$l('components.settings.unit', 'Unit')"
                >
                  <Select v-model="formUpdateTime.unit">
                    <Option
                      v-for="item in ['minutes', 'hours']"
                      :value="item"
                      :key="item"
                      >{{ item }}</Option
                    >
                  </Select>
                </FormItem>
              </Col>
            </Row>

            <FormItem style="position: relative; top: 150px">
              <div class="field is-grouped is-grouped-right">
                <div class="control">
                  <Button class="mr-2" @click="modalUpdateTime = false">{{
                    $l("global.cancel", "Cancel")
                  }}</Button>
                </div>
                <div class="control">
                  <Button
                    @click="handleClickUpdateTime('formUpdateTime')"
                    type="primary"
                  >
                    {{ $l("global.update", "Update") }}</Button
                  >
                </div>
              </div>
            </FormItem>
          </Form>
        </div>
      </box>
    </modal>

    <!-- Update copyright online -->

    <modal :show.sync="modalUpdateCopyrightOnline">
      <box>
        <div class="close-icon" @click="modalUpdateCopyrightOnline = false">
          <Icon size="25" type="ios-close" />
        </div>
        <div style="text-align: center; font-weight: bold">
          <span>{{
            $l(
              "components.settings.updateLicenseOnline",
              "Update license online"
            )
          }}</span>
        </div>

        <div style="margin: auto; width: 90%">
          <Form
            ref="formUpdateCopyrightOnline"
            :model="formUpdateCopyrightOnline"
            :rules="ruleValidate"
            label-position="top"
          >
            <FormItem ref="license" prop="license">
              <template slot="label">
                <strong>{{
                  $l("components.settings.license", "License")
                }}</strong>
              </template>

              <Input
                v-model="formUpdateCopyrightOnline.license"
                placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
              />
            </FormItem>

            <FormItem ref="server" prop="server">
              <template slot="label">
                <strong>{{
                  $l("components.settings.serverUpdate", "Server update")
                }}</strong>
              </template>

              <Input
                v-model="formUpdateCopyrightOnline.server"
                :placeholder="
                  $l('components.settings.serverUpdate', 'Server update')
                "
              />
            </FormItem>

            <FormItem ref="proxyOnline" prop="proxyOnline">
              <template slot="label">
                <strong>{{
                  $l("components.settings.proxyServer", "Proxy serer")
                }}</strong>
              </template>

              <Input
                v-model="formUpdateCopyrightOnline.proxyOnline"
                placeholder="https://api.edr.server.com:6767"
              />
            </FormItem>

            <FormItem>
              <div class="field is-grouped is-grouped-right">
                <div class="control">
                  <Button
                    :loading="loading"
                    class="mr-2"
                    @click="modalUpdateCopyrightOnline = false"
                    >{{ $l("global.cancel", "Cancel") }}</Button
                  >
                </div>
                <div class="control">
                  <Button
                    :loading="loading"
                    @click="
                      handleClickUpdateCopyrightOnline(
                        'formUpdateCopyrightOnline'
                      )
                    "
                    type="primary"
                  >
                    {{ $l("global.update", "Update") }}</Button
                  >
                </div>
              </div>
            </FormItem>
          </Form>
        </div>
      </box>
    </modal>

    <!-- Update copyright offline -->

    <modal :show.sync="modalUpdateCopyrightOffline">
      <box>
        <div class="close-icon" @click="modalUpdateCopyrightOffline = false">
          <Icon size="25" type="ios-close" />
        </div>
        <div style="text-align: center; font-weight: bold">
          <span>{{
            $l(
              "components.settings.updateLicenseOffline",
              "Update license offline"
            )
          }}</span>
        </div>

        <div style="margin: auto; width: 90%">
          <Form
            ref="formUpdateCopyrightOffline"
            :model="formUpdateCopyrightOffline"
            :rules="ruleValidate"
            label-position="top"
          >
            <FormItem ref="license" prop="license">
              <template slot="label">
                <strong>{{
                  $l("components.settings.license", "License")
                }}</strong>
              </template>

              <Input
                v-model="formUpdateCopyrightOffline.license"
                placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
              />
            </FormItem>

            <FormItem ref="licenseInfo" prop="licenseInfo">
              <template slot="label">
                <strong>{{
                  $l("components.settings.licenseInfor", "License infor")
                }}</strong>
              </template>

              <Input
                v-model="formUpdateCopyrightOffline.licenseInfo"
                type="textarea"
                :autosize="{ minRows: 5, maxRows: 10 }"
                placeholder="TkRjNE9UazRPVFUzTkRjNE1UZ3pOakkyTXprd056WTRPRGd4TlRVNU5qVTJNell6T0Rrd016WTBOakV5TURnMU5qQTVNelF3TXpVeU1UVXhPRFV4TkRBNU1EVTJNakF4TlRjMU16ZzFORFEwTkRFNE5UWTJNRGMwTVRBNU5qYzRORFEwTURnek56SXpNamd4TURNMk56WTFNemN6TWpjd05EY3dOak13TkRrM09ERXdPRGMwT1RBeU9UUTVNVEEwT0Rnek5qTXhPRFU0TkRVd01EUXlPVE01TXpnd056a3dORGN3TlRReE5UazROekUxT0RNeE1UYzFOams1T0RnM"
              />
            </FormItem>

            <FormItem>
              <div class="field is-grouped is-grouped-right">
                <div class="control">
                  <Button
                    :loading="loading"
                    class="mr-2"
                    @click="modalUpdateCopyrightOffline = false"
                    >{{ $l("global.cancel", "Cancel") }}</Button
                  >
                </div>
                <div class="control">
                  <Button
                    :loading="loading"
                    @click="
                      handleClickUpdateCopyrightOffline(
                        'formUpdateCopyrightOffline'
                      )
                    "
                    type="primary"
                  >
                    {{ $l("global.update", "Update") }}</Button
                  >
                </div>
              </div>
            </FormItem>
          </Form>
        </div>
      </box>
    </modal>

    <!-- Check connection -->

    <modal :show.sync="modalCheckConnection">
      <box>
        <div class="close-icon" @click="modalCheckConnection = false">
          <Icon size="25" type="ios-close" />
        </div>
        <div style="text-align: center; font-weight: bold">
          <span>{{
            $l("components.settings.checkConnection", "Check connection")
          }}</span>
        </div>

        <div style="margin: auto; width: 80%; margin-top: 20px">
          <Row :gutter="16">
            <Col v-if="!formCheckConnecttion.result" span="24">
              <span>{{
                $l(
                  "components.settings.checkConnectionToServerUpdate",
                  "Check connection to server update"
                )
              }}</span>
            </Col>

            <Col v-if="formCheckConnecttion.result" span="5">
              <span>{{ $l("components.settings.check", "Check") + ":" }}</span>
            </Col>

            <Col v-if="formCheckConnecttion.result" span="19">
              <span
                :class="{
                  'is-success': formCheckConnecttion.result === 'success',
                  'is-danger': formCheckConnecttion.result === 'fail',
                }"
                >{{
                  formCheckConnecttion.result === "success"
                    ? $l("components.settings.success", "Success")
                    : $l("components.settings.fail", "Fail")
                }}</span
              >
            </Col>

            <Col span="24">
              <div class="field is-grouped is-grouped-right">
                <div class="control">
                  <Button
                    @click="modalCheckConnection = false"
                    :loading="loading"
                    class="mr-2"
                    >{{ $l("global.cancel", "Cancel") }}</Button
                  >
                </div>
                <div class="control">
                  <Button
                    @click="checkConnection"
                    :loading="loading"
                    type="primary"
                  >
                    {{
                      formCheckConnecttion.result
                        ? $l("global.checkAgain", "Check again")
                        : $l("global.check", "Check")
                    }}</Button
                  >
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </box>
    </modal>
  </div>
</template>

<script>
import SettingsApi from "@/api/Settings";
import Format from "date-fns/format";
import moment from "moment";
import { Modal } from "@cyradar/ui";

import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Checkbox,
  Button,
  Form,
  FormItem,
  Input,
  Icon,
  Row,
  Col,
  Select,
  Option,
} from "iview";
export default {
  components: {
    Modal,
    Dropdown,
    DropdownMenu,
    DropdownItem,
    Checkbox,
    Button,
    Form,
    FormItem,
    Input,
    Icon,
    Row,
    Col,
    Select,
    Option,
  },

  computed: {
    defaultRule() {
      return {
        required: true,
        message: this.$l(
          "components.settings.cannotBeLeftBlank",
          "Cannot be left blank"
        ),
        trigger: "blur",
      };
    },

    ruleValidate() {
      return {
        server: [this.defaultRule],
        proxy: [this.defaultRule],
        unit: [
          {
            type: "string",
            validator: this.checkValidTime,
            trigger: "change",
          },
        ],

        interval: [
          {
            type: "number",
            validator: this.checkValidTime,
            trigger: "change",
          },
        ],

        license: [
          this.defaultRule,
          {
            type: "string",
            validator: this.checkValidLicense,
            required: true,
            trigger: "blur",
          },
        ],

        licenseInfo: [this.defaultRule],
      };
    },
  },

  data() {
    return {
      loading: false,
      // list interval

      listIntervalHours: Array.from({ length: 24 }, (_, index) => index + 1),
      listIntervalMinutes: Array.from({ length: 176 }, (_, index) => index + 5),

      information: {},
      formUpdateServer: {
        server: "",
      },
      formUpdateProxy: {},
      formUpdateTime: {
        interval: 20,
        unit: "hours",
      },

      formUpdateCopyrightOnline: {},
      formUpdateCopyrightOffline: {},
      formCheckConnecttion: {},

      // Modal
      modalUpdateServer: false,
      modalUpdateProxy: false,
      modalUpdateTime: false,
      modalUpdateCopyrightOnline: false,
      modalUpdateCopyrightOffline: false,
      modalCheckConnection: false,
    };
  },

  methods: {
    // Time

    dateDistance(date) {
      const now = new Date();
      const endISO = moment(date, moment.ISO_8601);
      const start = moment(now, "HH:mm:ss YYYY-MM-DD");
      const startISO = moment(start.format(), moment.ISO_8601);
      const diff = endISO.diff(startISO, "seconds");
      return diff;
    },

    renderTime(time) {
      return Format(time, "DD/MM/YYYY");
    },

    // Update Server

    showUpdateServer() {
      this.modalUpdateServer = true;
      if (this.information.server && this.information.server.length > 0) {
        this.formUpdateServer.serverUpdate = this.information.server.join(", ");
      }
    },

    updateServer() {
      SettingsApi.updateCopyright(this.$http, {
        servers: this.formUpdateServer.serverUpdate
          ? this.formUpdateServer.serverUpdate
              .split(",")
              .map((item) => item.trim())
          : [],
        proxyServer: this.information.proxyServer,
        interval: this.information.interval,
        unit: this.information.unit,
      }).then((res) => {
        this.$store.dispatch("NOTIFY", {
          type: "success",
          text: res.message,
          duration: 10000,
        });

        this.getCopyright();

        this.modalUpdateServer = false;
      });
    },

    handleClickUpdateServer(name) {
      this.$refs[name].validate((valid) => {
        if (valid) {
          this.updateServer();
        }
      });
    },

    // Update proxy

    showUpdateProxy() {
      this.modalUpdateProxy = true;
      if (this.information.proxyServer) {
        this.formUpdateProxy.proxy = this.information.proxyServer;
      }
    },

    updateProxy() {
      SettingsApi.updateCopyright(this.$http, {
        proxyServer: this.formUpdateProxy.proxy,
        server: this.information.server,
        interval: this.information.interval,
        unit: this.information.unit,
      }).then((res) => {
        this.$store.dispatch("NOTIFY", {
          type: "success",
          text: res.message,
          duration: 10000,
        });

        this.getCopyright();

        this.modalUpdateProxy = false;
      });
    },

    handleClickUpdateProxy(name) {
      this.$refs[name].validate((valid) => {
        if (valid) {
          this.updateProxy();
        }
      });
    },

    // Update Time

    checkValidTime(rule, value, callback) {
      if (!value) {
        callback(
          new Error(
            this.$l(
              "components.settings.cannotBeLeftBlank",
              "Cannot be left blank"
            )
          )
        );
      } else {
        callback();
      }
    },

    showUpdateTime() {
      this.modalUpdateTime = true;
      if (this.information.interval && this.information.unit) {
        this.formUpdateTime.interval = this.information.interval;
        this.formUpdateTime.unit = this.information.unit;
      }
    },

    updateTime() {
      SettingsApi.updateCopyright(this.$http, {
        server: this.information.server,
        proxyServer: this.information.proxyServer,
        interval: this.formUpdateTime.interval,
        unit: this.formUpdateTime.unit,
      }).then((res) => {
        this.$store.dispatch("NOTIFY", {
          type: "success",
          text: res.message,
          duration: 10000,
        });

        this.getCopyright();
        this.modalUpdateTime = false;
      });
    },

    handleClickUpdateTime(name) {
      this.$refs[name].validate((valid) => {
        if (valid) {
          this.updateTime();
        }
      });
    },

    // Update copyright online

    checkValidLicense(rule, value, callback) {
      const regex =
        /^[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}$/;
      if (!regex.test(value)) {
        callback(
          new Error(
            this.$l(
              "components.server.fingerprintValidFormat",
              "Fingerprint must be in the form XXXXX-XXX-XXXXX-XXXXX-XXXXX"
            )
          )
        );
      } else {
        callback();
      }
    },

    updateCopyRightOnline() {
      this.loading = true;
      SettingsApi.updateLicenseOnline(this.$http, {
        servers: this.formUpdateCopyrightOnline.server
          .split(",")
          .map((item) => item.trim()),
        proxy: this.formUpdateCopyrightOnline.proxyOnline,
        licensecode: this.formUpdateCopyrightOnline.license,
      })
        .then((res) => {
          this.$store.dispatch("NOTIFY", {
            type: "success",
            text: res.message,
            duration: 10000,
          });

          this.formUpdateCopyrightOnline = {};
          this.getCopyright();
          this.modalUpdateCopyrightOnline = false;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    handleClickUpdateCopyrightOnline(name) {
      this.$refs[name].validate((valid) => {
        if (valid) {
          this.updateCopyRightOnline();
        }
      });
    },

    // Update copyright offline

    updateCopyRightOffline() {
      this.loading = true;
      SettingsApi.updateLicenseOffline(this.$http, {
        licenseCode: this.formUpdateCopyrightOffline.license,
        licenseInfo: JSON.parse(
          this.formUpdateCopyrightOffline.licenseInfo.replace(
            /([\[:])?(\d+)([,\}\]])/g,
            '$1"$2"$3'
          )
        ),
      })
        .then((res) => {
          this.$store.dispatch("NOTIFY", {
            type: "success",
            text: res.message,
            duration: 10000,
          });

          this.formUpdateCopyrightOffline = {};
          this.getCopyright();
          this.modalUpdateCopyrightOffline = false;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    handleClickUpdateCopyrightOffline(name) {
      this.$refs[name].validate((valid) => {
        if (valid) {
          this.updateCopyRightOffline();
        }
      });
    },

    // Check connection
    showCheckConnection() {
      this.formCheckConnecttion = {};
      this.modalCheckConnection = true;
    },

    checkConnection() {
      this.loading = true;
      SettingsApi.checkConnection(this.$http)
        .then(() => {
          this.formCheckConnecttion = {
            result: "success",
          };

          this.loading = false;
        })

        .catch(() => {
          this.formCheckConnecttion = {
            result: "fail",
          };

          this.loading = false;
        });
    },

    // Get copyright

    getCopyright() {
      SettingsApi.getCopyright(this.$http).then((res) => {
        this.information = res;
      });
    },
  },

  created() {
    this.getCopyright();
  },
};
</script>

<style lang="scss" scoped>
.is-danger {
  color: red;
}

.is-success {
  color: #2d8cf0;
}

.button-text {
  color: #2d8cf0;
  cursor: pointer;
}

.close-icon {
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;

  &:hover {
    opacity: 0.5;
  }
}
</style>
